import React, { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout'
import { useBrandSettings, useUpdateBrandSettings } from '@/hooks/useBrand'
import type { UpdateBrandSettingsInput } from '@/types/brand'

export const AdminSettingsPage: React.FC = () => {
    const { data: brandSettings, isLoading } = useBrandSettings()
    const { mutate: updateSettings, isPending: isUpdating } = useUpdateBrandSettings()

    const [formData, setFormData] = useState<UpdateBrandSettingsInput>({
        name: '',
        slug: '',
        logoUrl: '',
        primaryColor: '#3B82F6',
        secondaryColor: '#1E40AF',
        domain: '',
        emailSender: '',
        certificateTemplate: '',
    })

    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')

    // Populate form when data loads
    useEffect(() => {
        if (brandSettings) {
            setFormData({
                name: brandSettings.name || '',
                slug: brandSettings.slug || '',
                logoUrl: brandSettings.logoUrl || '',
                primaryColor: brandSettings.primaryColor || '#3B82F6',
                secondaryColor: brandSettings.secondaryColor || '#1E40AF',
                domain: brandSettings.domain || '',
                emailSender: brandSettings.emailSender || '',
                certificateTemplate: brandSettings.certificateTemplate || '',
            })
        }
    }, [brandSettings])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setSuccessMessage('')
        setErrorMessage('')

        try {
            await updateSettings(formData, {
                onSuccess: () => {
                    setSuccessMessage('Brand settings updated successfully!')
                    setTimeout(() => setSuccessMessage(''), 3000)
                },
                onError: (error: any) => {
                    setErrorMessage(error?.message || 'Failed to update brand settings')
                },
            })
        } catch (error: any) {
            setErrorMessage(error?.message || 'Failed to update brand settings')
        }
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        )
    }

    return (
        <div>
            <PageHeader
                title="Pengaturan"
                subtitle="Konfigurasi platform LMS"
            />

            {/* Brand Settings Section */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-slate-200 dark:border-white/5 overflow-hidden shadow-sm">
                <div className="px-6 py-5 border-b border-slate-100 dark:border-white/5 bg-slate-50 dark:bg-white/[0.02]">
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <span className="material-symbols-outlined text-primary">store</span>
                        Brand Settings
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Konfigurasi identitas visual dan informasi akademi
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-8">
                    {/* Success/Error Messages */}
                    {successMessage && (
                        <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-sm font-medium flex items-center gap-2">
                            <span className="material-symbols-outlined text-[18px]">check_circle</span>
                            {successMessage}
                        </div>
                    )}
                    {errorMessage && (
                        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm font-medium flex items-center gap-2">
                            <span className="material-symbols-outlined text-[18px]">error</span>
                            {errorMessage}
                        </div>
                    )}

                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                Nama Akademi <span className="text-primary">*</span>
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all"
                                placeholder="Nama akademi"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                Slug <span className="text-primary">*</span>
                            </label>
                            <input
                                type="text"
                                name="slug"
                                value={formData.slug}
                                onChange={handleChange}
                                required
                                pattern="[a-z0-9-]+"
                                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all font-mono"
                                placeholder="nama-akademi"
                            />
                            <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-1.5 italic">Huruf kecil, angka, dan tanda hubung saja</p>
                        </div>
                    </div>

                    {/* Domain & Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                Domain <span className="text-primary">*</span>
                            </label>
                            <input
                                type="text"
                                name="domain"
                                value={formData.domain}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all font-mono"
                                placeholder="akademi.com"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                Email Pengirim <span className="text-primary">*</span>
                            </label>
                            <input
                                type="email"
                                name="emailSender"
                                value={formData.emailSender}
                                onChange={handleChange}
                                required
                                className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all font-mono"
                                placeholder="no-reply@akademi.com"
                            />
                        </div>
                    </div>

                    {/* Logo URL */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                            URL Logo
                        </label>
                        <input
                            type="url"
                            name="logoUrl"
                            value={formData.logoUrl}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all"
                            placeholder="https://cdn.example.com/logo.png"
                        />
                    </div>

                    {/* Colors */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                Warna Primer
                            </label>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                <div className="relative size-12 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm">
                                    <input
                                        type="color"
                                        name="primaryColor"
                                        value={formData.primaryColor}
                                        onChange={handleChange}
                                        className="absolute inset-0 size-full cursor-pointer border-0 p-0"
                                    />
                                </div>
                                <input
                                    type="text"
                                    value={formData.primaryColor}
                                    onChange={(e) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                                    className="flex-1 px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all font-mono"
                                    placeholder="#CC0001"
                                    pattern="^#[0-9A-Fa-f]{6}$"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                                Warna Sekunder
                            </label>
                            <div className="flex items-center gap-3">
                                <div className="relative size-12 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm">
                                    <input
                                        type="color"
                                        name="secondaryColor"
                                        value={formData.secondaryColor}
                                        onChange={handleChange}
                                        className="absolute inset-0 size-full cursor-pointer border-0 p-0"
                                    />
                                </div>
                                <input
                                    type="text"
                                    value={formData.secondaryColor}
                                    onChange={(e) => setFormData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                                    className="flex-1 px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all font-mono"
                                    placeholder="#E53535"
                                    pattern="^#[0-9A-Fa-f]{6}$"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Certificate Template */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                            Template Sertifikat
                        </label>
                        <input
                            type="text"
                            name="certificateTemplate"
                            value={formData.certificateTemplate}
                            onChange={handleChange}
                            className="w-full px-4 py-2.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-primary/50 transition-all"
                            placeholder="template-sertifikat.pdf"
                        />
                        <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-1.5 italic">Path ke template PDF sertifikat</p>
                    </div>

                    {/* Preview */}
                    <div className="p-5 rounded-lg bg-slate-50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-4">Preview Warna:</p>
                        <div className="flex items-center gap-4">
                            <div
                                className="h-14 flex-1 rounded-lg flex items-center justify-center text-white font-bold shadow-lg"
                                style={{ backgroundColor: formData.primaryColor }}
                            >
                                <span className="drop-shadow-md">Warna Primer</span>
                            </div>
                            <div
                                className="h-14 flex-1 rounded-lg flex items-center justify-center text-white font-bold shadow-lg"
                                style={{ backgroundColor: formData.secondaryColor }}
                            >
                                <span className="drop-shadow-md">Warna Sekunder</span>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end pt-6 border-t border-slate-100 dark:border-white/5">
                        <button
                            type="submit"
                            disabled={isUpdating}
                            className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white rounded-lg font-bold text-sm shadow-lg shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100"
                        >
                            {isUpdating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    Menyimpan...
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-[20px]">save</span>
                                    Simpan Perubahan
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default AdminSettingsPage
